import { useLayoutEffect, useRef, useState } from 'react'
import type { SignalTag } from '@bloo-team/api-client'
import { TagChip } from '../workbook/blocks/SignalTags'

/**
 * A signal's resolved tags as badges, every name in the title (and, for
 * assistive tech, in visually hidden text). Nothing when there is none.
 *
 * `fit` (a table cell, a list row): one line — as many badges as the cell
 * holds, then `+N` for the rest. A hidden ruler of every badge is measured
 * against the cell's width, so the count follows the cell as it resizes.
 * Without `fit` (the drawer) every badge shows and they wrap.
 */
type BadgeTag = Pick<SignalTag, 'id' | 'value' | 'color'>

export function SignalTagChips({ tags, fit = false }: { tags: readonly BadgeTag[]; fit?: boolean }) {
  if (!tags.length) return null
  const title = tags.map((t) => t.value).join(', ')
  if (!fit) {
    return (
      <span className="sg-tagchips" data-wrap="" title={title}>
        {tags.map((t) => (
          <TagChip key={t.id} value={t.value} color={t.color} />
        ))}
      </span>
    )
  }
  return <FitTags tags={tags} title={title} />
}

function FitTags({ tags, title }: { tags: readonly BadgeTag[]; title: string }) {
  const box = useRef<HTMLSpanElement>(null)
  const probe = useRef<HTMLSpanElement>(null)
  const [shown, setShown] = useState(tags.length)

  useLayoutEffect(() => {
    const el = box.current
    const ruler = probe.current
    if (!el || !ruler) return
    const measure = () => {
      const width = el.clientWidth
      const items = [...ruler.children] as HTMLElement[]
      const more = items.pop() // the last probe child is a "+N" stand-in
      // No layout (jsdom, hidden): show every badge.
      if (!width || !items.length || !items[0]!.offsetWidth) return setShown(tags.length)
      const gap = parseFloat(getComputedStyle(ruler).columnGap) || 0
      const moreW = (more?.offsetWidth ?? 0) + gap
      let used = 0
      let n = 0
      for (let i = 0; i < items.length; i++) {
        const w = items[i]!.offsetWidth + (i ? gap : 0)
        const reserve = i < items.length - 1 ? moreW : 0
        if (used + w + reserve > width) break
        used += w
        n++
      }
      setShown(Math.max(1, n))
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [title, tags.length])

  const rest = tags.length - shown
  return (
    <span ref={box} className="sg-tagchips" data-fit="" title={title}>
      {tags.slice(0, shown).map((t) => (
        <TagChip key={t.id} value={t.value} color={t.color} />
      ))}
      {rest > 0 ? (
        <>
          <span className="muted sg-tag-more" aria-hidden="true">
            +{rest}
          </span>
          {/* The title is mouse-only: the names behind +N, for keyboard and screen-reader users. */}
          <span className="bui-sr-only">, also {tags.slice(shown).map((t) => t.value).join(', ')}</span>
        </>
      ) : null}
      {/* The ruler: same badge boxes, text drawn by CSS (attr) so each name is in the DOM once. */}
      <span ref={probe} className="sg-tag-probe" aria-hidden="true">
        {tags.map((t) => (
          <span key={t.id} className="sg-badge tag-badge" data-label={t.value} />
        ))}
        <span className="muted sg-tag-more" data-label={`+${tags.length}`} />
      </span>
    </span>
  )
}
